/**
 * Comportements transverses Vestiaire Saint-Honoré.
 *
 *  1. Révélation des blocs à l'entrée dans le viewport
 *  2. Fondu des images au chargement
 *  3. Favoris persistants (localStorage) + tiroir de consultation
 *
 * Aucune dépendance. Tout dégrade proprement : sans JS, les blocs restent
 * visibles (voir .vsh-reveal dans vsh-luxe.css) et les favoris disparaissent
 * simplement de l'interface.
 */

/* ------------------------------------------------------------------ *
 * 1. Révélation au défilement
 * ------------------------------------------------------------------ */
(() => {
  const targets = Array.from(document.querySelectorAll('.vsh-reveal'));
  if (!targets.length) return;

  const showAll = () => targets.forEach((el) => (el.dataset.in = 'true'));

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !('IntersectionObserver' in window)) {
    showAll();
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.dataset.in = 'true';
        io.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  targets.forEach((el) => io.observe(el));

  // Filet 1 — révèle tout de suite ce qui est déjà dans le viewport, par simple
  // mesure géométrique. Ne dépend pas de l'observer (qui ne se déclenche pas,
  // par exemple, dans un onglet jamais peint).
  const revealVisible = () => {
    const h = window.innerHeight || document.documentElement.clientHeight;
    targets.forEach((el) => {
      if (el.dataset.in === 'true') return;
      const r = el.getBoundingClientRect();
      if (r.top < h && r.bottom > 0) {
        el.dataset.in = 'true';
        io.unobserve(el);
      }
    });
  };
  revealVisible();
  window.addEventListener('load', revealVisible, { once: true });

  // Filet 2 — au-delà de ce délai, plus aucune raison de garder quoi que ce
  // soit masqué : mieux vaut perdre l'animation que perdre le contenu.
  setTimeout(showAll, 2500);
})();

/* ------------------------------------------------------------------ *
 * 2. Fondu des images
 * ------------------------------------------------------------------ */
(() => {
  document.querySelectorAll('img.vsh-img-fade').forEach((img) => {
    if (img.complete && img.naturalWidth) img.dataset.loaded = 'true';
    else img.addEventListener('load', () => (img.dataset.loaded = 'true'), { once: true });
  });
})();

/* ------------------------------------------------------------------ *
 * 3. Favoris
 * ------------------------------------------------------------------ */
const VshFavourites = (() => {
  const KEY = 'vsh:favourites';

  const read = () => {
    try {
      const raw = localStorage.getItem(KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
    } catch {
      // Navigation privée ou stockage plein : on dégrade sans casser la page.
      return [];
    }
  };

  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch {
      /* ignoré volontairement */
    }
  };

  const has = (handle) => read().includes(handle);

  const toggle = (handle) => {
    const list = read();
    const i = list.indexOf(handle);
    if (i === -1) list.push(handle);
    else list.splice(i, 1);
    write(list);
    sync();
    return i === -1;
  };

  const sync = () => {
    const list = read();
    document.querySelectorAll('[data-vsh-fav]').forEach((btn) => {
      const on = list.includes(btn.dataset.vshFav);
      btn.setAttribute('aria-pressed', String(on));
      const label = btn.querySelector('[data-vsh-fav-label]');
      if (label) label.textContent = on ? 'Retirer des favoris' : 'Mettre de côté';
      if (!label) {
        btn.setAttribute(
          'aria-label',
          on ? 'Retirer cette pièce des favoris' : 'Ajouter cette pièce aux favoris'
        );
      }
    });
    document.querySelectorAll('[data-vsh-fav-count]').forEach((el) => {
      el.dataset.n = String(list.length);
      el.textContent = String(list.length);
    });
  };

  return { read, has, toggle, sync };
})();

(() => {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-vsh-fav]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const added = VshFavourites.toggle(btn.dataset.vshFav);
    if (added) {
      btn.dataset.just = 'true';
      setTimeout(() => delete btn.dataset.just, 420);
    }
  });

  VshFavourites.sync();

  // Ouverture du tiroir des favoris
  document.addEventListener('click', async (e) => {
    const link = e.target.closest('[data-vsh-fav-link]');
    if (!link) return;
    e.preventDefault();
    await openDrawer();
  });

  async function openDrawer() {
    let dialog = document.querySelector('[data-vsh-fav-drawer]');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'vsh-fav-drawer';
      dialog.setAttribute('data-vsh-fav-drawer', '');
      dialog.innerHTML = `
        <div class="vsh-fav-drawer__head">
          <h2 class="vsh-fav-drawer__title">Mes favoris</h2>
          <button type="button" class="vsh-fav-drawer__close" aria-label="Fermer">
            <svg viewBox="0 0 14 14" aria-hidden="true" fill="none" stroke="currentColor">
              <path d="M1 1l12 12M13 1L1 13"/>
            </svg>
          </button>
        </div>
        <div class="vsh-fav-drawer__body" data-vsh-fav-body></div>`;
      document.body.appendChild(dialog);
      dialog.querySelector('.vsh-fav-drawer__close').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (ev) => {
        if (ev.target === dialog) dialog.close();
      });
    }

    const body = dialog.querySelector('[data-vsh-fav-body]');
    const handles = VshFavourites.read();

    if (!handles.length) {
      body.innerHTML =
        `<p class="vsh-lede">Aucune pièce mise de côté pour l'instant.</p>` +
        `<p style="margin-block-start:1.5rem"><a class="vsh-btn vsh-btn--dark" href="/collections/all"><span>Parcourir le vestiaire</span></a></p>`;
      if (!dialog.open) dialog.showModal();
      return;
    }

    body.innerHTML = `<p class="vsh-fav-drawer__loading">Chargement…</p>`;
    if (!dialog.open) dialog.showModal();

    const items = await Promise.all(
      handles.map((h) =>
        fetch(`/products/${encodeURIComponent(h)}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );

    const rows = items
      .map((p, i) => {
        if (!p) return '';
        const img = p.featured_image
          ? `<img src="${p.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2')}" alt="" loading="lazy">`
          : '<span></span>';
        const price = (p.price / 100).toLocaleString('fr-FR', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'EUR',
        });
        return `<div class="vsh-fav-row">
            <a class="vsh-fav-row__media" href="${p.url}">${img}</a>
            <div>
              <span class="vsh-search__house">${p.vendor}</span>
              <a class="vsh-fav-row__name" href="${p.url}">${p.title}</a>
              <span class="vsh-fav-row__price">${price}</span>
            </div>
            <button type="button" class="vsh-fav-row__remove" data-vsh-fav="${handles[i]}" aria-label="Retirer">×</button>
          </div>`;
      })
      .join('');

    body.innerHTML = rows || `<p class="vsh-lede">Ces pièces ne sont plus disponibles.</p>`;
    VshFavourites.sync();

    body.addEventListener(
      'click',
      (ev) => {
        if (ev.target.closest('[data-vsh-fav]')) setTimeout(() => openDrawer(), 60);
      },
      { once: true }
    );
  }
})();
